import {
  fromUTCtoZoneFormatted,
  STORE_KEY_LIST,
  storeGet
} from 'mailup-react-helpers'
import { useEffect } from 'react'
import { useIntl } from 'react-intl'
import { useRecoilCallback, useRecoilState, useRecoilValue } from 'recoil'

import styled from '@emotion/styled'
import {
  Button,
  BUTTON_VARIANT_SECONDARY,
  DataListHeader,
  DataListRow,
  Flex,
  Grid,
  GridCell,
  IconFACheck,
  IconFATimes,
  Modal,
  ModalBody,
  ModalFoot,
  ModalHead,
  SKELETON_LOADER_VARIANT_ONWHITE,
  SkeletonLoader,
  SPACING_16,
  SPACING_24,
  SPACING_32,
  SpacingVertical,
  Spinner,
  Text,
  TEXT_VARIANT_BOLD,
  TEXT_VARIANT_LIGHT,
  Title,
  TITLE_VARIANT_M,
  TITLE_VARIANT_MODAL,
  TruncateAndTooltip
} from 'mailup-web-components'

import { SOURCE_ID_TYPE_FILE } from 'Constants/index'
import { recallActivityCallback } from 'Pages/SourceEdit/recoil/callbacks/callbacks'
import { MAX_CALLS, sourceState } from 'Pages/SourceEdit/recoil/state/source'
import { modalShowHistoryState } from '../../recoil/state/modals'

const ModalShowHistory = () => {
  const intl = useIntl()

  const [{ show, otherActivity, callNum }, setShowModal] = useRecoilState(
    modalShowHistoryState
  )
  const recallActivity = useRecoilCallback(recallActivityCallback, [])

  const closeModal = () => {
    setShowModal((prev) => ({ ...prev, show: false }))
  }

  useEffect(() => {
    if (callNum > -1 && callNum <= MAX_CALLS + 1) {
      setTimeout(() => recallActivity(callNum), 2000)
    }
  }, [callNum])
  return (
    <Modal
      show={show}
      showClose
      closeOnOverlayClick
      onClose={closeModal}
      data-mu-test='source-edit__modal-show-history'
    >
      <ModalHead>
        <Title variant={TITLE_VARIANT_MODAL}>
          {intl.formatMessage({ id: 'Load.ContentsSouces.UpdateHistory' })}
        </Title>
        <SpacingVertical variant={SPACING_32} />
      </ModalHead>
      <ModalBody scrollable>
        <Flex maxHeight='450px' flexDirection='column'>
          <UpdateHistoryBody />
          {otherActivity && <OtherActivityBody />}
        </Flex>
      </ModalBody>
      <ModalFoot>
        <Button kind={BUTTON_VARIANT_SECONDARY} onClick={closeModal}>
          {intl.formatMessage({
            id: 'Load.Common.Close'
          })}
        </Button>
      </ModalFoot>
    </Modal>
  )
}

export default ModalShowHistory

const UpdateHistoryBody = () => {
  const intl = useIntl()

  const { TimeZoneCode: tzcode } = storeGet(STORE_KEY_LIST)
  const { loading, loaded, updateHistory } = useRecoilValue(
    modalShowHistoryState
  )
  const { idType } = useRecoilValue(sourceState)

  const heads = [
    {
      label: intl.formatMessage({
        id: 'Load.MailupCommon.Status'
      }),
      span: 6
    },
    {
      label:
        idType === SOURCE_ID_TYPE_FILE
          ? intl.formatMessage({
              id: 'Load.Common.UploadDate'
            })
          : intl.formatMessage({
              id: 'Load.ContentsSouces.LastChanheStatusDate'
            }),
      span: 6
    }
  ]

  return (
    <>
      <Title variant={TITLE_VARIANT_M}>
        {intl.formatMessage({ id: 'Load.ContentsSouces.LastSourceUpdate' })}
      </Title>
      <SpacingVertical variant={SPACING_16} />
      <DataListHeader heads={heads} />
      <div>
        {updateHistory.map(({ Status, UpdateDate }, index) => (
          <DataListRow key={'elem_' + index}>
            <Grid cols={12}>
              <GridCell span={6}>
                <Text variant={TEXT_VARIANT_BOLD}>
                  {loaded && Status === 1 && (
                    <Flex
                      alignItems='center'
                      columnGap='8px'
                      justifyContent='flex-start'
                    >
                      <YesIcon />
                      {intl.formatMessage({ id: 'Load.Common.Updated' })}
                    </Flex>
                  )}
                  {loaded && Status === 2 && (
                    <Flex
                      alignItems='center'
                      columnGap='8px'
                      justifyContent='flex-start'
                    >
                      <NoIcon />
                      {intl.formatMessage({ id: 'Load.Common.NotUpdated' })}
                    </Flex>
                  )}
                  {loading && (
                    <SkeletonLoader variant={SKELETON_LOADER_VARIANT_ONWHITE} />
                  )}
                </Text>
              </GridCell>
              <GridCell span={6}>
                <Text variant={TEXT_VARIANT_LIGHT}>
                  {intl.formatMessage({ id: 'Load.Common.UntilTo' })}{' '}
                  {loaded &&
                    fromUTCtoZoneFormatted(
                      UpdateDate,
                      tzcode,
                      'YYYY-MM-DD HH:mm'
                    )}
                  {loading && (
                    <SkeletonLoader variant={SKELETON_LOADER_VARIANT_ONWHITE} />
                  )}
                </Text>
              </GridCell>
            </Grid>
          </DataListRow>
        ))}
      </div>
    </>
  )
}

const OtherActivityBody = () => {
  const intl = useIntl()

  const { TimeZoneCode: tzcode } = storeGet(STORE_KEY_LIST)
  const { loading, loaded, otherActivity } = useRecoilValue(
    modalShowHistoryState
  )

  const heads = [
    {
      label: intl.formatMessage({
        id: 'Load.MailupCommon.Status'
      }),
      span: 4
    },
    {
      label: intl.formatMessage({
        id: 'Load.Common.UploadDate'
      }),
      span: 4
    },
    {
      label: intl.formatMessage({
        id: 'Load.Common.FileName'
      }),
      span: 4
    }
  ]

  if (!otherActivity || otherActivity.length === 0) return null

  return (
    <>
      <SpacingVertical variant={SPACING_24} />
      <Title variant={TITLE_VARIANT_M}>
        {intl.formatMessage({ id: 'Load.ContentsSouces.LastSourceUpdate' })}
      </Title>
      <SpacingVertical variant={SPACING_16} />
      <DataListHeader heads={heads} />
      <div>
        {otherActivity.map(({ Status, UpdateDate, ActivityData }, index) => (
          <DataListRow key={'elem_' + index}>
            <Grid cols={12}>
              <GridCell span={4}>
                <Text variant={TEXT_VARIANT_BOLD}>
                  {loaded && (Status === 1 || Status === 0) && (
                    <Flex
                      alignItems='center'
                      columnGap='8px'
                      justifyContent='flex-start'
                    >
                      <LoadingIcon />
                      {intl.formatMessage({ id: 'Load.Common.InProgress' })}
                    </Flex>
                  )}
                  {loaded && Status === 2 && (
                    <Flex
                      alignItems='center'
                      columnGap='8px'
                      justifyContent='flex-start'
                    >
                      <YesIcon />
                      {intl.formatMessage({ id: 'Load.Common.Updated' })}
                    </Flex>
                  )}
                  {loaded && Status === -1 && (
                    <Flex
                      alignItems='center'
                      columnGap='8px'
                      justifyContent='flex-start'
                    >
                      <NoIcon />
                      {intl.formatMessage({ id: 'Load.Common.UpdateFail' })}
                    </Flex>
                  )}
                  {loading && (
                    <SkeletonLoader variant={SKELETON_LOADER_VARIANT_ONWHITE} />
                  )}
                </Text>
              </GridCell>
              <GridCell span={4}>
                <Text variant={TEXT_VARIANT_LIGHT}>
                  {intl.formatMessage({ id: 'Load.Common.UntilTo' })}{' '}
                  {loaded &&
                    fromUTCtoZoneFormatted(
                      UpdateDate,
                      tzcode,
                      'YYYY-MM-DD HH:mm'
                    )}
                  {loading && (
                    <SkeletonLoader variant={SKELETON_LOADER_VARIANT_ONWHITE} />
                  )}
                </Text>
              </GridCell>
              <GridCell span={4}>
                <Text variant={TEXT_VARIANT_LIGHT}>
                  {loaded && (
                    <TruncateAndTooltip
                      text={JSON.parse(ActivityData).UserFilename}
                    />
                  )}
                  {loading && (
                    <SkeletonLoader variant={SKELETON_LOADER_VARIANT_ONWHITE} />
                  )}
                </Text>
              </GridCell>
            </Grid>
          </DataListRow>
        ))}
      </div>
    </>
  )
}

const YesIcon = styled(IconFACheck)`
  border-radius: 50%;
  width: 21px;
  height: 21px;
  display: inline-grid;
  place-content: center;
  background-color: var(--tea-green);
  color: var(--sushi);

  svg {
    top: 0 !important;
    width: 15px;
    height: 15px;
  }
`

const LoadingIcon = styled(Spinner)`
  width: 21px;
  height: 21px;
  display: inline-grid;
  place-content: center;
`

const NoIcon = styled(IconFATimes)`
  border-radius: 50%;
  width: 21px;
  height: 21px;
  display: inline-grid;
  place-content: center;
  background-color: var(--vivid-tangerine);
  color: var(--sunset-orange);

  svg {
    top: 1px !important;
    width: 13px;
    height: 13px;
  }
`
